import { FccAdAlertStatus, FccAdAlertType } from '@backoffice-frontend/graphql'
import { useAdVehicleStream } from '../AdVehicleStream/useAdVehicleStream'
import { AllAlertsAreSolvedActionCard } from './ActionCards/AllAlertsAreSolvedActionCard'
import { PassengerUnbuckledActionCard } from './ActionCards/PassengerUnbuckledActionCard/PassengerUnbuckledActionCard'
import { ServiceManagementActionCard } from './ActionCards/ServiceManagementActionCard'
import { WaypointAttendanceActionCard } from './ActionCards/WaypointAttendanceActionCard'
import type { AdVehicleStreamVehicleFragment } from './AdVehicleStream.hook'

type FccAdAlertFragment = NonNullable<
  AdVehicleStreamVehicleFragment['fccOperatorAdAlerts']
>['alerts'][0]
type ActiveActionCardProps = {
  vehicle: AdVehicleStreamVehicleFragment
  onClose: VoidFunction
}

const getActionCardByAlertType = (
  alert: FccAdAlertFragment,
  vehicle: AdVehicleStreamVehicleFragment,
  onClose: VoidFunction,
) => {
  const mapping: Partial<Record<FccAdAlertType, () => React.JSX.Element>> = {
    [FccAdAlertType.PassengerUnbuckled]: () => (
      <PassengerUnbuckledActionCard
        vehicle={vehicle}
        isAlertSolved={alert.status === FccAdAlertStatus.Closed}
        onClose={onClose}
      />
    ),
    [FccAdAlertType.ServiceCancellationOngoing]: () => (
      <ServiceManagementActionCard vehicle={vehicle} />
    ),
    [FccAdAlertType.TripCancellationOngoing]: () => (
      <ServiceManagementActionCard vehicle={vehicle} />
    ),
    [FccAdAlertType.EmergencyStop]: () => (
      <ServiceManagementActionCard vehicle={vehicle} />
    ),
    [FccAdAlertType.WaypointAttendance]: () => (
      <WaypointAttendanceActionCard vehicle={vehicle} />
    ),
    [FccAdAlertType.CabinSafetyCheck]: () => (
      <WaypointAttendanceActionCard vehicle={vehicle} />
    ),
    [FccAdAlertType.ManualNoShow]: () => (
      <WaypointAttendanceActionCard vehicle={vehicle} />
    ),
  }
  return mapping[alert.type]
}

/**
 * Displays the best suited action card based on a vehicle.
 * It considers the active alerts retrieved and ordered by priority from the backend.
 * Then it will find the best suited action card for the given situation.
 *
 * Some alert types may not have an alert card, hence a fallthrough behaviour is established.
 * This allows a decoupling between what the backend considers most important to look at and what the frontend can actually handle.
 */
export const ActiveActionCard = ({
  vehicle,
  onClose,
}: ActiveActionCardProps) => {
  const { pendingUpdate } = useAdVehicleStream()
  const allAlertsAreSolved = vehicle.fccOperatorAdAlerts?.alerts.every(
    alert => alert.status === FccAdAlertStatus.Closed,
  )

  if (allAlertsAreSolved) {
    return (
      <AllAlertsAreSolvedActionCard
        pendingUpdate={pendingUpdate}
        closeModal={onClose}
      />
    )
  }

  // Find the first unsolved alert with an available action card
  const unsolvedAlerts =
    vehicle.fccOperatorAdAlerts?.alerts?.filter(
      alert => alert.status !== FccAdAlertStatus.Closed,
    ) ?? []
  for (const alert of unsolvedAlerts) {
    const actionCard = getActionCardByAlertType(alert, vehicle, onClose)
    if (actionCard) {
      return actionCard()
    }
  }

  // If no alerts or no action card was found for any alert
  return (
    <AllAlertsAreSolvedActionCard
      pendingUpdate={pendingUpdate}
      closeModal={onClose}
    />
  )
}
